import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../services/constants";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg: #0a0a0a;
    --surface: #111111;
    --surface2: #1a1a1a;
    --border: #2a2a2a;
    --accent: #e8ff3c;
    --accent-dim: rgba(232,255,60,0.10);
    --text: #f0f0f0;
    --muted: #666666;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-page {
    min-height: 100vh;
    background: var(--bg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    padding: 2rem 1rem;
    position: relative;
    overflow: hidden;
  }

  .auth-page::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(var(--border) 1px, transparent 1px),
      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 48px 48px;
    opacity: 0.35;
    pointer-events: none;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2.8rem 2.5rem;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 0 60px rgba(232,255,60,0.04);
  }

  .auth-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.2rem;
    letter-spacing: 0.1em;
    color: var(--accent);
    text-align: center;
    display: block;
    margin-bottom: 0.3rem;
  }

  .auth-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1.6rem;
    letter-spacing: 0.06em;
    color: var(--text);
    text-align: center;
    margin-bottom: 0.4rem;
  }

  .auth-subtitle {
    text-align: center;
    color: var(--muted);
    font-size: 0.82rem;
    margin-bottom: 2rem;
    line-height: 1.5;
  }

  .auth-divider {
    height: 1px;
    background: var(--border);
    margin: 1.6rem 0;
  }

  .auth-input {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text);
    padding: 0.95rem 1rem;
    font-size: 0.95rem;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    margin-bottom: 1.5rem;
  }

  .auth-input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-dim);
  }

  .auth-btn {
    width: 100%;
    background: var(--accent);
    color: #000;
    border: none;
    border-radius: 6px;
    padding: 0.85rem;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 1rem;
    letter-spacing: 0.08em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s, transform 0.15s;
  }

  .auth-btn:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
  }

  .auth-btn:disabled {
    background: var(--surface2);
    color: var(--muted);
    cursor: not-allowed;
  }

  .auth-footer-text {
    text-align: center;
    margin-top: 1.5rem;
    font-size: 0.8rem;
    color: var(--muted);
  }

  .auth-footer-text a {
    color: var(--accent);
    text-decoration: none;
    font-weight: 500;
  }

  .auth-footer-text a:hover {
    text-decoration: underline;
  }
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${StandardURL}/iMusic/auth/forgot-pwd`,
        { email }
      );

      toast.success(response.data.response);
      navigate("/");
    } catch (error) {
      toast.error(
        error?.response?.data?.response ||
          "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>

      <div className="auth-page">
        <div className="auth-card">
          <span className="auth-logo">MUSICA</span>

          <h2 className="auth-title">Forgot Password</h2>

          <p className="auth-subtitle">
            Enter the email address associated with your account.
            <br />
            We'll send you instructions to reset your password.
          </p>

          <div className="auth-divider" />

          <form onSubmit={handleSubmit}>
            <input
              type="email"
              className="auth-input"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <button
              type="submit"
              className="auth-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <Oval
                  height={20}
                  width={20}
                  color="#000"
                  visible={true}
                  ariaLabel="loading-indicator"
                  secondaryColor="#000"
                />
              ) : (
                "Send Reset Link"
              )}
            </button>
          </form>

          <p className="auth-footer-text">
            Remember your password?{" "}
            <a href="/">Back to Login</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;